import React from "react";
import { useEditCameraStore } from "../editCameraStore/editCameraStore";
import {
  UpdatedAlertStreamTypes,
  UpdatedAlertStreams,
} from "../EditCameraUtils/editCameraInterfaces";
import { RECEIVER_CONFIGS, ReceiverKey, streamAlerts } from "./receiverConfigs";

type LocalAlerts = Partial<Record<ReceiverKey, Record<string, any>>>;

/**
 * Edit-mode state for every receiver card (ENG-2483). One generic version of
 * what the Alert Settings edit tab did per field per integration:
 *
 *  - change: update the local copy, record `{...local copy, ...change}` as
 *    the alert's PATCH body in updatedAlertStreams, and flag the type dirty.
 *    The PATCH (and markSiteUnsynced) happens in the store on Save, unchanged.
 *  - blur (text fields): copy the local value (or "") into
 *    cameraStreamsPatchObject.
 *  - commit (choices): both at once, as the old switches did.
 */
export const useAlertReceiverEditing = () => {
  const {
    cameraStreams,
    cameraStreamsSelectedIndex,
    cameraStreamsObject,
    cameraStreamsPatchObject,
    setCameraStreamsPatchObject,
    setUpdatedAlertStreams,
    setUpdatedAlertStreamTypes,
  } = useEditCameraStore();

  const [localAlerts, setLocalAlerts] = React.useState<LocalAlerts>({});

  React.useEffect(() => {
    if (!cameraStreamsPatchObject) return;
    const next: LocalAlerts = {};
    (Object.keys(RECEIVER_CONFIGS) as ReceiverKey[]).forEach((key) => {
      const config = RECEIVER_CONFIGS[key];
      if (
        !streamAlerts(
          cameraStreamsObject?.[cameraStreamsSelectedIndex],
          config.alertsKey
        )
      )
        return;
      const alerts = streamAlerts(
        cameraStreamsPatchObject[cameraStreamsSelectedIndex],
        config.alertsKey
      );
      if (!alerts) return;
      const byId: Record<string, any> = {};
      alerts.forEach((alert: any) => {
        if (alert?.id) {
          byId[alert.id] = config.initLocal(alert);
        }
      });
      next[key] = byId;
    });
    setLocalAlerts((prev) => ({ ...prev, ...next }));
  }, [
    cameraStreamsPatchObject,
    cameraStreamsSelectedIndex,
    cameraStreamsObject,
    cameraStreams,
  ]);

  const patchStoredAlert = (
    key: ReceiverKey,
    alertIndex: number,
    values: Record<string, any>
  ) => {
    const { alertsKey } = RECEIVER_CONFIGS[key];
    setCameraStreamsPatchObject((previousState: any) => {
      const newState = [...(previousState || [])];
      const newStream = { ...newState[cameraStreamsSelectedIndex] };
      const newAlerts = [...(newStream[alertsKey] || [])];
      newAlerts[alertIndex] = { ...newAlerts[alertIndex], ...values };
      newStream[alertsKey] = newAlerts;
      newState[cameraStreamsSelectedIndex] = newStream;
      return newState;
    });
  };

  /**
   * Record a change. `localValues` defaults to `values`; SureView's server
   * keeps "" locally but sends the raw selection, as before.
   */
  const changeAlert = (
    key: ReceiverKey,
    alertId: string | number,
    values: Record<string, any>,
    localValues: Record<string, any> = values
  ) => {
    setLocalAlerts((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        [alertId]: { ...prev[key]?.[alertId], ...localValues },
      },
    }));
    setUpdatedAlertStreams((prev: UpdatedAlertStreams) => ({
      ...prev,
      [key]: {
        ...prev[key],
        [alertId]: { ...localAlerts[key]?.[alertId], ...values },
      },
    }));
    setUpdatedAlertStreamTypes((prev: UpdatedAlertStreamTypes) => ({
      ...prev,
      [key]: true,
    }));
  };

  /** Text field blur: store the local value (or ""). */
  const blurField = (
    key: ReceiverKey,
    alertId: string | number,
    alertIndex: number,
    field: string
  ) => {
    patchStoredAlert(key, alertIndex, {
      [field]: localAlerts[key]?.[alertId]?.[field] || "",
    });
  };

  /** Choice controls: record and store in one go. */
  const commitAlert = (
    key: ReceiverKey,
    alertId: string | number,
    alertIndex: number,
    values: Record<string, any>
  ) => {
    changeAlert(key, alertId, values);
    patchStoredAlert(key, alertIndex, values);
  };

  const localAlert = (key: ReceiverKey, alertId: string | number) =>
    localAlerts[key]?.[alertId];

  return { localAlert, changeAlert, blurField, commitAlert };
};
